import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator, GoogleAuthProvider } from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import {
  initializeFirestore, connectFirestoreEmulator,
  persistentLocalCache, persistentMultipleTabManager,
} from 'firebase/firestore'

/**
 * On our own addresses, sign in on the address the person is actually on:
 * sailnorse.org and app.sailnorse.org are the same app, and the Worker
 * forwards /__/auth/ on both. Matching the visited host is the one rule that
 * cannot drift; the build variable covers dev and the firebaseapp.com URL.
 */
function ownDomain(): string | null {
  if (typeof location === 'undefined') return null
  const h = location.hostname
  return h === 'sailnorse.org' || h === 'app.sailnorse.org' ? h : null
}

const cfg = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  // Must be our own domain. Safari 16.1+, Firefox 109+ and Chrome 115+ block
  // third-party storage, which breaks Firebase's redirect flow when the auth
  // handler lives on *.firebaseapp.com — exactly the case an installed iOS PWA
  // hits. Same-origin sidesteps it entirely.
  authDomain: ownDomain() ?? import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FB_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID,
}

export const ALLOWED_DOMAIN = import.meta.env.VITE_ALLOWED_DOMAIN ?? 'roland-story.k12.ia.us'

export const app = initializeApp(cfg)
export const auth = getAuth(app)
// Offline persistence is not a nice-to-have here. Attendance gets taken in a
// shop with two bars of signal, and a write somebody believes was saved but
// that never left the phone is worse than no app at all. Firestore queues the
// write; the UI's job is to be honest about the queue (see ui/States.tsx).
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})

/** Cloud Functions the app calls directly (the student roster upload). */
export const functions = getFunctions(app, 'us-central1')

/** Students' proof that a project host agreed. The only thing the app stores as a file. */
export const storage = getStorage(app)

export const googleProvider = new GoogleAuthProvider()
// A hint to Google's account picker, not a security control — trivially
// bypassed. The real enforcement is the beforeSignIn blocking function and
// firestore.rules.
googleProvider.setCustomParameters({ hd: ALLOWED_DOMAIN, prompt: 'select_account' })

// Ports match the non-default set in firebase.json. Moved off Firebase's
// defaults (9099 / 8080 / …) so this project's emulators can run alongside
// another Firebase project's emulators on the same machine without a port
// clash — the default ports are common enough that two projects both using
// them is the normal case, not an edge case.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9199', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8090)
  connectFunctionsEmulator(functions, '127.0.0.1', 5501)
  connectStorageEmulator(storage, '127.0.0.1', 9299)
}
